import api from '@api/api';
import { LoginInfo, TokenInfo, UserState } from '@models/user';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { clearPermissionList } from '@store/reducers/permissionSlice';
import { REMEMBER_USER_INFO } from '@utils/const';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import md5 from 'md5';

export const logIn = createAsyncThunk(
  'userSlice/logIn',
  async (accountInfo: LoginInfo) => {
    const { sellerNumber, account, password } = accountInfo;

    const {
      data: { code: loginCode, msg: loginMsg, data: token },
    } = await api.post('/api/config-service/user/login', {
      account,
      password: md5(password),
      sellerNumber,
      smsCaptchaPass: true,
    });

    if (loginCode !== 0) {
      throw new Error(loginMsg);
    }

    // 使用傳回的 token 取得 userInfo
    const {
      data: { code: userInfoCode, msg: userInfoMsg, data: userInfoData },
    } = await api.post(
      '/api/config-service/user/info',
      {},
      {
        headers: {
          Authorization: token,
        },
      },
    );

    if (userInfoCode !== 0) {
      throw new Error(userInfoMsg);
    }
    const { name } = userInfoData;

    // 然後把 token 和 userInfo 資料一起傳給 logIn.fulfilled reducer
    return { token, name };
  },
);

export const logOut = createAsyncThunk(
  'userSlice/logOut',
  async (_, thunkAPI) => {
    try {
      const res = await api.post(`/api/config-service/user/logout`);
      // Use thunkAPI.dispatch to dispatch the clearPermissionList action
      thunkAPI.dispatch(clearPermissionList());
      return res.data;
    } catch (error: any) {
      // Handle or throw error
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const userSlice = createSlice({
  name: 'userSlice',
  initialState: {
    accessToken: null,
    name: null,
    sellerId: null,
    userId: null,
    account: null,
  } as UserState,
  reducers: {
    // 傳入一個物件
    updateUserInfo: (state, action) => {
      // update state
      console.log('action payload', action.payload);
      Object.keys(action.payload).forEach((key) => {
        const value = action.payload[key];
        console.log(`key: ${key}, value: ${value}`);
        state[key as keyof UserState] = value;
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logIn.fulfilled, (state, action) => {
      const { token, name } = action.payload;
      const { sellerId, userId, account } = jwtDecode(token) as TokenInfo;

      state.accessToken = token;
      state.sellerId = sellerId;
      state.userId = userId;
      state.name = name;
      state.account = account;

      const languagePreference = localStorage.getItem('language');
      const debugLogPreference = localStorage.getItem('DEBUG_LOG');
      const engModePreference = localStorage.getItem('engMode');
      const rememberAccount = localStorage.getItem(REMEMBER_USER_INFO.ACCOUNT);
      const rememberSellerNumber = localStorage.getItem(
        REMEMBER_USER_INFO.SELLER_NUMBER,
      );

      // 登入成功，先清除 localStorage 再重新導向到首頁
      localStorage.clear();

      localStorage.setItem('language', languagePreference || 'jaJP');
      localStorage.setItem('DEBUG_LOG', debugLogPreference || 'false');
      localStorage.setItem('engMode', engModePreference || 'false');
      localStorage.setItem('sellerId', sellerId.toString());
      localStorage.setItem('userId', userId.toString());
      localStorage.setItem('accessToken', token.toString());
      localStorage.setItem('name', name.toString());
      localStorage.setItem('account', account.toString());
      localStorage.setItem(REMEMBER_USER_INFO.ACCOUNT, rememberAccount || '');
      localStorage.setItem(
        REMEMBER_USER_INFO.SELLER_NUMBER,
        rememberSellerNumber || '',
      );
    });

    builder.addCase(logOut.fulfilled, (state, action) => {
      console.log('logOut, response:', action.payload);

      delete axios.defaults.headers.common.Authorization;

      localStorage.removeItem('name');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('sellerId');
      localStorage.removeItem('permissionList');
      localStorage.removeItem('areaData');
      localStorage.removeItem('account');

      window.location.reload();
    });
  },
});

export const { updateUserInfo } = userSlice.actions;

export default userSlice.reducer;
